import { useState } from "react";
import { LifeBuoy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGERS = ["1Password", "Bitwarden", "LastPass", "Dashlane", "Apple Passwords / iCloud Keychain", "Google Password Manager", "KeePass / KeePassXC", "Passbolt", "Proton Pass", "NordPass", "Other"];

const TIPS = {
  "1Password": "Print the Emergency Kit (Secret Key + account URL) and note who is the Family Organizer — they can recover your account.",
  Bitwarden: "Set up Emergency Access (Settings → Emergency access) for a trusted contact; note the wait time you chose.",
  LastPass: "Add an Emergency Access contact and note the wait period; keep your recovery one-time password somewhere safe.",
  Dashlane: "Note your account email; Dashlane has no recovery of the master password — record where it's written down.",
  "Apple Passwords / iCloud Keychain": "Add a Recovery Contact or Legacy Contact in Apple ID settings and note who it is.",
  "Google Password Manager": "Set up Inactive Account Manager and note your recovery phone/email.",
  Passbolt: "Back up your private key (Profile → Keys → Download) and passphrase separately, and record which admin can recover the account — without the key the vault can't be opened.",
  "KeePass / KeePassXC": "Record where the .kdbx database file and the key file live, plus where the master password is stored offline.",
};

const FIELDS = [
  ["account_email", "Account email / username", "you@example.com"],
  ["master_hint", "Master password hint (never the password itself)", "e.g. the phrase from our first road trip"],
  ["kit_location", "Emergency Kit / Secret Key / key file location", "e.g. fireproof safe, top drawer"],
  ["recovery_codes_location", "Recovery codes location", "e.g. sealed envelope with the will"],
  ["trusted_contact", "Who has emergency access", "e.g. Sam (Family Organizer)"],
  ["emergency_access", "How emergency access works", "e.g. request access, 7-day wait, then full vault"],
];

export const EmergencyKitModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ manager: "1Password", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/passwords/emergency-kit`, form);
      toast.success("Emergency kit card saved (encrypted)");
      onCreated?.();
      onClose();
      setForm({ manager: "1Password", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="emergency-kit-modal" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-primary" /> Password manager emergency kit</DialogTitle>
          <DialogDescription>The one card your executor needs: which manager holds everything, and how to get in. Stored encrypted.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={form.manager} onValueChange={(v) => set("manager", v)}>
            <SelectTrigger data-testid="kit-manager-select" className="bg-card"><SelectValue /></SelectTrigger>
            <SelectContent>{MANAGERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          {TIPS[form.manager] && <p data-testid="kit-tip" className="text-xs text-muted-foreground rounded-lg bg-primary/5 border border-primary/20 p-2.5">{TIPS[form.manager]}</p>}
          {FIELDS.map(([k, label, ph]) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground">{label}</label>
              <Input data-testid={`kit-${k}-input`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="bg-card mt-1" />
            </div>
          ))}
          <Textarea data-testid="kit-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything else the executor should know" className="bg-card" rows={2} />
          <Button data-testid="kit-save-btn" onClick={submit} disabled={busy} className="w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save encrypted kit card
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
